import { type SelectProps } from 'antd';
import { useMemo } from 'react';
import { type UserGroupOptionsDataFragment } from '../../api/fragments/UserGroupOptionsData';
import { useGetUserGroupOptionsQuery } from '../../api/queries/getUserGroupOptions';
import FormFields from '../../themes/admin/Fields/FormFields';
import { useCompany } from '../contexts/CompanyContextManager';
import { type SelectFieldProps } from './SelectField';

export type UserGroupSelectFieldProps = Omit<SelectFieldProps, 'children'> & {
    userGroups?: UserGroupOptionsDataFragment[];
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.label.toString().toLowerCase().includes(inputValue.toLowerCase());

const UserGroupSelectField = ({ userGroups: userGroupsFromProps, ...props }: UserGroupSelectFieldProps) => {
    // get company context
    const company = useCompany(true);

    const { data, loading } = useGetUserGroupOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { companyId: company?.id } },
        skip: !!userGroupsFromProps,
    });

    const userGroups = useMemo(
        () => userGroupsFromProps || data?.userGroups?.items || [],
        [data?.userGroups?.items, userGroupsFromProps]
    );

    const options = userGroups.map(userGroup => ({
        value: userGroup.id,
        key: userGroup.id,
        label: userGroup.displayName,
    }));

    return (
        <FormFields.SelectField
            {...props}
            filterOption={filterOption}
            loading={!userGroups && loading}
            options={options}
        />
    );
};

export default UserGroupSelectField;
